import PhotoIconSVG from "@/public/PhotoButtonMark1.svg";
import SCloseButton from 'components/framework/SCloseButton';
import CMSImage, { CMSCroppedImage } from 'components/images/Image';
import { MouseEventHandler, RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigation, Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

import { ICustomImage } from "@filmweb/sanity-types";
import { getOrgSize } from "lib/utils/getOrgSize";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import styles from "./GalleryContainer.module.css";


//#region [Props]
type GalleryContainerProps = {
    images?: ICustomImage[];
    overridenImage?: ICustomImage;
};
//#endregion

//#region [Component]
export default function GalleryContainer({ overridenImage, images }: GalleryContainerProps) {
    const [showGallery, setShowGallery] = useState(false);

    useEffect(() => {
        if (showGallery) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "";
        }
    }, [showGallery]);

    const imageArray = useMemo(() => {
        if ((images?.length ?? 0) === 0) {
            console.debug("Images length === 0");
            return null;
        }
        // filter out all non-horizontal images
        const imgs = images!.filter(img => {
            if (!img?.asset?._id) {
                return false;
            }
            const orgSize = getOrgSize(img.asset._id);
            if (orgSize.aspect <= 1) {
                return false;
            }
            if (overridenImage?._id === img.asset._id) {
                return false;
            }
            return true;
        })

        if (overridenImage) {
            if ((imgs?.length ?? 0) === 0) {
                return [overridenImage];
            }
            return [overridenImage, ...imgs!];
        }
        if ((imgs?.length ?? 0) < 2) {
            // there is 1 or 0 images. Return as is
            return imgs;
        }

        if ((imgs?.length ?? 0) < 3) {
            // there is 1 or 0 images. Return as is
            return [imgs![1], imgs![0]]; // re-arrange the first 2 images
        }

        return [imgs![1], imgs![0], ...imgs!.slice((imgs!.length - 2) * -1)]; // re-arrange the first 2 images
    }, [images, overridenImage]);

    const containerRef = useRef<HTMLDivElement>();

    const onClose = useCallback<MouseEventHandler>((event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        if ((event.target as HTMLElement).tagName === "BUTTON" || event.target === containerRef.current) {
            setShowGallery(false);
        }
    }, [containerRef]);

    if ((imageArray?.length ?? 0) === 0) {
        return null;
    }

    return (<>
        <button className={styles.GalleryButton} onClick={() => setShowGallery(true)} title="Vis bildegalleri" type='button'>
            <CMSCroppedImage image={imageArray![0]} width={400} heightRatio={1} isFilmweb />
            <PhotoIconSVG />
        </button>
        {showGallery && <div className={styles.GalleryContainer} ref={containerRef as RefObject<HTMLDivElement>} onClick={onClose}>
            <div className={styles.GalleryPositioningContainer}>
                <SCloseButton className={styles.GalleryCloser} onClick={onClose} $isOpen />
                <Swiper className={styles.Swiper} modules={[Navigation, Pagination]} navigation pagination loop>
                    {imageArray!.map(image => <SwiperSlide key={image.asset._id}>
                        <CMSImage className={styles.GalleryImage} image={image} sizes="(min-width: 1440px) 1440px, 100vw" isFilmweb />
                    </SwiperSlide>)}
                </Swiper>
            </div>
        </div>}
    </>);

}
//#endregion
